import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Truck, Plus, Edit, Trash2, Mail, Phone, MapPin, Globe, Building2, FileText, Users, X, History, Power, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { AuditLogDialog } from '../../components/AuditLogDialog';

const SuppliersPage = () => {
  const { locations, selectedLocation, competencies } = useData();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [updatingCui, setUpdatingCui] = useState(null);
  const [auditDialog, setAuditDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateFields, setBulkUpdateFields] = useState({});
  const [bulkUpdateValues, setBulkUpdateValues] = useState({});
  
  const emptyForm = {
    xrp_code: '',
    name: '',
    location_id: 'all',
    phone_fixed: '',
    phone_mobile: '',
    fax: '',
    email: '',
    website: '',
    registry_number: '',
    cif: '',
    locality: '',
    county: '',
    country: 'Romania',
    address: '',
    notes: '',
    competency_ids: [],
    contacts: [{ name: '', position: '', phone: '', email: '' }]
  };
  
  const [formData, setFormData] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const showInactive = statusFilter !== 'active';
      const response = await api.get(`/suppliers${showInactive ? '?show_inactive=true' : ''}`);
      let data = response.data;
      if (statusFilter === 'inactive') data = data.filter(s => s.is_active === false);
      else if (statusFilter === 'active') data = data.filter(s => s.is_active !== false);
      setSuppliers(data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/suppliers/${editItem.id}`, formData);
        toast.success('Furnizor actualizat!');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Furnizor creat!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      xrp_code: item.xrp_code || '',
      name: item.name || '',
      location_id: item.location_id || 'all',
      phone_fixed: item.phone_fixed || item.phone || '',
      phone_mobile: item.phone_mobile || '',
      fax: item.fax || '',
      email: item.email || '',
      website: item.website || '',
      registry_number: item.registry_number || '',
      cif: item.cif || '',
      locality: item.locality || '',
      county: item.county || '',
      country: item.country || 'Romania',
      address: item.address || '',
      notes: item.notes || '',
      competency_ids: item.competency_ids || [],
      contacts: item.contacts?.length > 0 ? item.contacts : [{ name: '', position: '', phone: '', email: '' }]
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('Furnizor sters!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.patch(`/suppliers/${id}/toggle-active`);
      toast.success(currentStatus ? 'Furnizor dezactivat' : 'Furnizor activat');
      fetchData();
    } catch (error) {
      toast.error('Eroare la schimbarea statusului');
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedIds.length) return;
    const action = isActive ? 'activa' : 'dezactiva';
    if (!window.confirm(`Sigur doriti sa ${action}ti ${selectedIds.length} furnizori?`)) return;
    try {
      await api.patch('/suppliers/bulk-status', { ids: selectedIds, is_active: isActive });
      toast.success(`${selectedIds.length} furnizori ${isActive ? 'activati' : 'dezactivati'}`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la operatia bulk');
    }
  };

  const handleBulkUpdateFields = async () => {
    const activeFields = Object.keys(bulkUpdateFields).filter(k => bulkUpdateFields[k]);
    if (!activeFields.length) { toast.error('Selectati cel putin un camp'); return; }
    const fields = {};
    activeFields.forEach(k => { fields[k] = bulkUpdateValues[k] ?? ''; });
    try {
      const res = await api.patch('/suppliers/bulk-update-fields', { ids: selectedIds, fields });
      toast.success(`${res.data.modified} furnizori actualizati (${res.data.fields_updated.length} campuri)`);
      setShowBulkUpdateDialog(false);
      setBulkUpdateFields({});
      setBulkUpdateValues({});
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea in masa');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Sigur doriti sa stergeti DEFINITIV ${selectedIds.length} furnizori? Aceasta actiune nu poate fi anulata!`)) return;
    try {
      await api.patch('/suppliers/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} furnizori stersi`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergerea in masa');
    }
  };

  const handleUpdateFromANAF = async (supplierId) => {
    setUpdatingCui(supplierId);
    try {
      const res = await api.post(`/company-data/update-supplier/${supplierId}`);
      const updatedFields = res.data.updated_fields || [];
      const fieldLabels = {
        registry_number: 'Nr.Reg.Com.',
        locality: 'Localitate',
        county: 'Judet',
        address: 'Adresa',
        phone_fixed: 'Telefon',
        fax: 'Fax',
        postal_code: 'Cod postal',
        caen_code: 'Cod CAEN',
        caen_description: 'Descriere CAEN',
        vat_payer: 'Platitor TVA',
        anaf_status: 'Status ANAF',
        registration_date: 'Data inregistrare',
        is_active: 'Activ',
        country: 'Tara'
      };
      const fieldNames = updatedFields
        .filter(f => f !== 'openapi_updated_at')
        .map(f => fieldLabels[f] || f);
      if (fieldNames.length > 0) {
        toast.success(`Actualizat: ${fieldNames.join(', ')}`);
      } else {
        toast.info(res.data.message);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la actualizare ANAF');
    } finally {
      setUpdatingCui(null);
    }
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { name: '', position: '', phone: '', email: '' }]
    });
  };

  const removeContact = (index) => {
    if (formData.contacts.length <= 1) return;
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, i) => i !== index)
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, contacts: newContacts });
  };

  const toggleCompetency = (compId) => {
    if (formData.competency_ids.includes(compId)) {
      setFormData({ ...formData, competency_ids: formData.competency_ids.filter(id => id !== compId) });
    } else {
      setFormData({ ...formData, competency_ids: [...formData.competency_ids, compId] });
    }
  };

  // Custom filter function for suppliers - combines multiple fields for search
  const suppliersWithSearchFields = useMemo(() => {
    return suppliers.map(s => ({
      ...s,
      // Combined phone field for filtering
      _phone_search: [s.phone_mobile, s.phone_fixed, s.fax].filter(Boolean).join(' '),
      // Combined locality field for filtering
      _locality_search: [s.locality, s.county, s.country, s.address].filter(Boolean).join(' ').toLowerCase()
    }));
  }, [suppliers]);

  const tableColumns = [
    { key: 'code', label: 'Cod', sortable: true, filterable: true,
      render: (value) => value ? <Badge variant="outline" className="font-mono text-xs bg-blue-50 text-blue-700">{value}</Badge> : '-'
    },
    { key: 'xrp_code', label: 'Cod Furnizor XRP', sortable: true, filterable: true,
      render: (value) => value ? <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 font-mono text-xs">{value}</Badge> : '-'
    },
    { key: 'name', label: 'Denumire', sortable: true, filterable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-purple-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { key: 'cif', label: 'CIF', sortable: true, filterable: true,
      render: (value) => value ? <Badge variant="outline" className="font-mono">{value}</Badge> : '-'
    },
    { key: 'registry_number', label: 'Nr.Reg.Com.', sortable: true, filterable: true,
      render: (value) => value ? <span className="font-mono text-xs">{value}</span> : '-'
    },
    { key: '_locality_search', label: 'Localitate', sortable: true, filterable: true,
      render: (_, row) => {
        const parts = [row.locality, row.county].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '-';
      }
    },
    { key: '_phone_search', label: 'Telefon', sortable: true, filterable: true,
      render: (_, row) => (row.phone_mobile || row.phone_fixed) ? 
        <span className="flex items-center gap-1 text-sm"><Phone className="w-3 h-3 text-slate-400" />{row.phone_mobile || row.phone_fixed}</span> : '-'
    },
    { key: 'email', label: 'Email', sortable: true, filterable: true,
      render: (value) => value ? <span className="flex items-center gap-1 text-sm"><Mail className="w-3 h-3 text-slate-400" />{value}</span> : '-'
    },
    { key: 'is_active', label: 'Status', sortable: true,
      render: (value) => value === false
        ? <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Inactiv</Badge>
        : <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Activ</Badge>
    },
    { key: 'website', label: 'Website', sortable: false,
      render: (value) => value ? <span className="flex items-center gap-1 text-sm text-blue-600"><Globe className="w-3 h-3" />{value.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span> : '-'
    },
    { key: 'contacts', label: 'Contacte', sortable: false,
      render: (value) => value?.length > 0 ? <Badge>{value.length} contacte</Badge> : '-'
    },
    {
      key: 'actions', label: 'Actiuni', sortable: false, filterable: false, width: 'w-32',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleActive(row.id, row.is_active !== false)}
            title={row.is_active === false ? 'Activeaza' : 'Dezactiveaza'}
            data-testid={`toggle-supplier-${row?.code}`}
            className={row.is_active === false ? 'text-green-600' : 'text-orange-500'}
          >
            <Power className="w-4 h-4" />
          </Button>
          {(row.cif || row.cui) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleUpdateFromANAF(row.id)}
              disabled={updatingCui === row.id}
              title="Actualizeaza date din ANAF"
              className="text-indigo-600"
            >
              {updatingCui === row.id ? <span className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin inline-block" /> : <Globe className="w-4 h-4" />}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setAuditDialog(row)} title="Istoric Modificari" className="text-amber-600" data-testid={`audit-supplier-${row?.code}`}><History className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )
    }
  ];

  return (
    <>
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Furnizori Servicii</h1>
          <p className="text-slate-600 mt-1">Gestioneaza furnizorii ({suppliers.length} total)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1" data-testid="status-filter-suppliers">
            {[{v:'active',l:'Activi'},{v:'inactive',l:'Inactivi'},{v:'all',l:'Toti'}].map(f => (
              <button key={f.v} onClick={() => setStatusFilter(f.v)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusFilter === f.v ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`filter-supplier-${f.v}`}
              >{f.l}</button>
            ))}
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditItem(null); setFormData(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12"><Plus className="w-5 h-5 mr-2" />FURNIZOR NOU</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editItem ? 'Editeaza Furnizor' : 'Adauga Furnizor Servicii'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Code, XRP Code, Name & Location */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Cod</Label>
                  <Input 
                    value={editItem ? (editItem.code || '') : '(auto-generat)'} 
                    disabled
                    className="h-11 bg-slate-100 text-slate-500 font-mono"
                    data-testid="supplier-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cod Furnizor XRP</Label>
                  <Input 
                    value={formData.xrp_code} 
                    onChange={(e) => setFormData({ ...formData, xrp_code: e.target.value })} 
                    className="h-11 font-mono"
                    placeholder="FZ001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nume *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    required 
                    className="h-11"
                    placeholder="Denumire furnizor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Disponibil pentru locatii</Label>
                  <Select value={formData.location_id || 'all'} onValueChange={(v) => setFormData({ ...formData, location_id: v })}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Toate locatiile si fabricile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate locatiile si fabricile</SelectItem>
                      {locations?.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Phones */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Telefon fix</Label>
                  <Input 
                    value={formData.phone_fixed} 
                    onChange={(e) => setFormData({ ...formData, phone_fixed: e.target.value })} 
                    className="h-11"
                    placeholder="0234123456"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon mobil</Label>
                  <Input 
                    value={formData.phone_mobile} 
                    onChange={(e) => setFormData({ ...formData, phone_mobile: e.target.value })} 
                    className="h-11"
                    placeholder="0743123456"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fax</Label>
                  <Input 
                    value={formData.fax} 
                    onChange={(e) => setFormData({ ...formData, fax: e.target.value })} 
                    className="h-11"
                    placeholder="0234123457"
                  />
                </div>
              </div>

              {/* Row 3: Web & Registry */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      value={formData.website} 
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })} 
                      className="h-11 pl-10"
                      placeholder="http://www.example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nr.reg.com.</Label>
                  <Input 
                    value={formData.registry_number} 
                    onChange={(e) => setFormData({ ...formData, registry_number: e.target.value })} 
                    className="h-11"
                    placeholder="J22/25/1997"
                  />
                </div>
              </div>

              {/* Row 4: Email & CIF */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      type="email"
                      value={formData.email} 
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                      className="h-11 pl-10"
                      placeholder="mail@company.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>CIF</Label>
                  <Input 
                    value={formData.cif} 
                    onChange={(e) => setFormData({ ...formData, cif: e.target.value })} 
                    className="h-11"
                    placeholder="RO 1234567"
                  />
                </div>
              </div>

              {/* Row 5: Location details */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Localitate</Label>
                  <Input 
                    value={formData.locality} 
                    onChange={(e) => setFormData({ ...formData, locality: e.target.value })} 
                    className="h-11"
                    placeholder="Iasi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Judet</Label>
                  <Input 
                    value={formData.county} 
                    onChange={(e) => setFormData({ ...formData, county: e.target.value })} 
                    className="h-11"
                    placeholder="Iasi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tara</Label>
                  <Input 
                    value={formData.country} 
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })} 
                    className="h-11"
                    placeholder="Romania"
                  />
                </div>
              </div>

              {/* Row 6: Address */}
              <div className="space-y-2">
                <Label>Adresa</Label>
                <Textarea 
                  value={formData.address} 
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                  rows={2}
                  placeholder="Strada, numar, bloc, apartament"
                />
              </div>

              {/* ANAF Info (read-only display) */}
              {editItem && (editItem.caen_code || editItem.vat_payer !== undefined || editItem.anaf_status || editItem.registration_date) && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-2">
                  <Label className="flex items-center gap-2 text-indigo-700">
                    <Globe className="w-4 h-4" />
                    Informatii ANAF
                  </Label>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {editItem.vat_payer !== undefined && editItem.vat_payer !== null && (
                      <div>
                        <span className="text-xs text-slate-500">Platitor TVA</span>
                        <p className="font-medium">{editItem.vat_payer === true || editItem.vat_payer === 'true' ? 'Da' : (editItem.vat_payer === false || editItem.vat_payer === 'false' ? 'Nu' : String(editItem.vat_payer))}</p>
                      </div>
                    )}
                    {editItem.caen_code && (
                      <div>
                        <span className="text-xs text-slate-500">Cod CAEN</span>
                        <p className="font-medium">{editItem.caen_code}{editItem.caen_description ? ` - ${editItem.caen_description}` : ''}</p>
                      </div>
                    )}
                    {editItem.anaf_status && (
                      <div>
                        <span className="text-xs text-slate-500">Status ANAF</span>
                        <p className="font-medium">{editItem.anaf_status}</p>
                      </div>
                    )}
                    {editItem.registration_date && (
                      <div>
                        <span className="text-xs text-slate-500">Data inregistrare</span>
                        <p className="font-medium">{editItem.registration_date}</p>
                      </div>
                    )}
                    {editItem.openapi_updated_at && (
                      <div>
                        <span className="text-xs text-slate-500">Ultima actualizare ANAF</span>
                        <p className="font-medium text-green-600">{new Date(editItem.openapi_updated_at).toLocaleString('ro-RO')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notite sau activitati</Label>
                <Textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  rows={2}
                  placeholder="Descriere activitati, note..."
                />
              </div>

              <Separator />

              {/* Competencies */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Competente
                </Label>
                <div className="flex flex-wrap gap-2">
                  {competencies?.map(comp => (
                    <Badge 
                      key={comp.id}
                      variant={formData.competency_ids.includes(comp.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleCompetency(comp.id)}
                    >
                      {comp.name}
                    </Badge>
                  ))}
                  {(!competencies || competencies.length === 0) && (
                    <p className="text-sm text-slate-500">Nu exista competente definite</p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Contacts Table */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contacte furnizor
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addContact}>
                    <Plus className="w-4 h-4 mr-1" /> Adauga contact
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-medium">Nr.</th>
                        <th className="text-left p-2 font-medium">Nume</th>
                        <th className="text-left p-2 font-medium">Pozitie</th>
                        <th className="text-left p-2 font-medium">Telefon</th>
                        <th className="text-left p-2 font-medium">Email</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.contacts.map((contact, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2 text-slate-500">{String(idx + 1).padStart(2, '0')}.</td>
                          <td className="p-1">
                            <Input 
                              value={contact.name} 
                              onChange={(e) => updateContact(idx, 'name', e.target.value)}
                              className="h-9"
                              placeholder="Nume contact"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              value={contact.position} 
                              onChange={(e) => updateContact(idx, 'position', e.target.value)}
                              className="h-9"
                              placeholder="Pozitie"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              value={contact.phone} 
                              onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                              className="h-9"
                              placeholder="0743123456"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              type="email"
                              value={contact.email} 
                              onChange={(e) => updateContact(idx, 'email', e.target.value)}
                              className="h-9"
                              placeholder="mail@company.com"
                            />
                          </td>
                          <td className="p-1">
                            {formData.contacts.length > 1 && (
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeContact(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Inapoi</Button>
                <Button type="submit" className="btn-primary">Salveaza</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-actions-suppliers">
          <span className="text-sm font-medium text-blue-800">{selectedIds.length} selectati</span>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => { setBulkUpdateFields({}); setBulkUpdateValues({}); setShowBulkUpdateDialog(true); }} data-testid="bulk-update-fields-suppliers">
              <RefreshCw className="w-4 h-4 mr-1" /> Actualizare in masa
            </Button>
            <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleBulkStatus(true)} data-testid="bulk-activate-suppliers">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Activeaza
            </Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleBulkStatus(false)} data-testid="bulk-deactivate-suppliers">
              <XCircle className="w-4 h-4 mr-1" /> Dezactiveaza
            </Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={handleBulkDelete} data-testid="bulk-delete-suppliers">
              <Trash2 className="w-4 h-4 mr-1" /> Sterge
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable 
          data={suppliersWithSearchFields} 
          columns={tableColumns} 
          pageSize={10} 
          searchable={true} 
          exportable={true} 
          printable={true} 
          selectable={true}
          onSelectionChange={(ids) => setSelectedIds(ids)}
          onRowClick={handleEdit} 
          emptyMessage="Nu exista furnizori" 
        />
      )}
    </div>

    {/* Bulk Update Fields Dialog */}
    <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Actualizare in masa</DialogTitle>
          <DialogDescription>Selectati campurile pe care doriti sa le actualizati pentru {selectedIds.length} furnizori</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {[
            { key: 'location_id', label: 'Locatie', type: 'select' },
            { key: 'phone_fixed', label: 'Telefon Fix', type: 'text' },
            { key: 'phone_mobile', label: 'Telefon Mobil', type: 'text' },
            { key: 'fax', label: 'Fax', type: 'text' },
            { key: 'email', label: 'Email', type: 'text' },
            { key: 'website', label: 'Website', type: 'text' },
            { key: 'locality', label: 'Localitate', type: 'text' },
            { key: 'county', label: 'Judet', type: 'text' },
            { key: 'country', label: 'Tara', type: 'text' },
            { key: 'address', label: 'Adresa', type: 'text' },
            { key: 'notes', label: 'Observatii', type: 'text' },
          ].map(field => (
            <div key={field.key} className={`rounded-lg border p-3 transition-colors ${bulkUpdateFields[field.key] ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <Checkbox id={`bulk-sup-${field.key}`} checked={!!bulkUpdateFields[field.key]}
                  onCheckedChange={(checked) => {
                    setBulkUpdateFields(prev => ({ ...prev, [field.key]: checked }));
                    if (!checked) setBulkUpdateValues(prev => { const n = { ...prev }; delete n[field.key]; return n; });
                  }} data-testid={`bulk-sup-check-${field.key}`} />
                <label htmlFor={`bulk-sup-${field.key}`} className="text-sm font-medium cursor-pointer flex-1">{field.label}</label>
              </div>
              {bulkUpdateFields[field.key] && (
                <div className="mt-2 pl-6">
                  {field.key === 'location_id' ? (
                    <Select value={bulkUpdateValues.location_id || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, location_id: v }))}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza locatie" /></SelectTrigger>
                      <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare ${field.label.toLowerCase()}`} data-testid={`bulk-sup-val-${field.key}`} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>Anuleaza</Button>
          <Button className="btn-primary" onClick={handleBulkUpdateFields} disabled={!Object.values(bulkUpdateFields).some(v => v)} data-testid="bulk-update-submit-suppliers">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizeaza {selectedIds.length} furnizori
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AuditLogDialog
      open={!!auditDialog}
      onOpenChange={(open) => !open && setAuditDialog(null)}
      entityType="supplier"
      entityId={auditDialog?.id}
      entityName={auditDialog ? `${auditDialog.code || ''} - ${auditDialog.name}` : ''}
    />
    </>
  );
};

export default SuppliersPage;
